import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DayRange } from '@/components/core';
import { endOfPreviousMonthDay, orgToday, startOfPreviousMonthDay } from '../../utils/task-date';

/** "Any customer / project / department". */
export const ALL_VALUE = 'all';

export type TimesheetFilters = {
  range: DayRange;
  departmentId: string;
  customerId: string;
  projectId: string;
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Team Timesheets' range and filters, kept in the URL (`from`, `to`, `department`, `customer`,
 * `project`) so the employee page opened from a row keeps them (Component Map › Employee timesheet
 * page) and Back returns to the same rollup. The range defaults to last month.
 */
export const useTimesheetFilters = () => {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<TimesheetFilters>(() => {
    const today = orgToday();
    const from = params.get('from') ?? '';
    const to = params.get('to') ?? '';
    return {
      range:
        DAY.test(from) && DAY.test(to) && from <= to
          ? { from, to }
          : { from: startOfPreviousMonthDay(today), to: endOfPreviousMonthDay(today) },
      departmentId: params.get('department') ?? '',
      customerId: params.get('customer') ?? ALL_VALUE,
      projectId: params.get('project') ?? ALL_VALUE,
    };
  }, [params]);

  const setFilters = useCallback(
    (patch: Partial<TimesheetFilters>) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const put = (key: string, value: string | undefined, empty: string) =>
            value && value !== empty ? next.set(key, value) : next.delete(key);
          if (patch.range) {
            next.set('from', patch.range.from);
            next.set('to', patch.range.to);
          }
          if ('departmentId' in patch) put('department', patch.departmentId, '');
          if ('customerId' in patch) put('customer', patch.customerId, ALL_VALUE);
          if ('projectId' in patch) put('project', patch.projectId, ALL_VALUE);
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );

  return { filters, setFilters, search: params.toString() };
};
